import { isLocalDevelopment } from './is-local-host';

/**
 * Enhanced console logging that only outputs in development/local environments
 */
interface DevConsoleLog {
  /**
   * Log general information (only in development)
   */
  log: (...args: unknown[]) => void;

  /**
   * Log warnings (only in development)
   */
  warn: (...args: unknown[]) => void;

  /**
   * Log errors (only in development)
   */
  error: (...args: unknown[]) => void;

  /**
   * Log debug information (only in development)
   */
  debug: (...args: unknown[]) => void;

  /**
   * Log informational messages (only in development)
   */
  info: (...args: unknown[]) => void;

  /**
   * Log with a custom prefix (only in development)
   */
  logWithPrefix: (prefix: string, ...args: unknown[]) => void;

  /**
   * Group logs (only in development)
   */
  group: (...args: unknown[]) => void;

  /**
   * GroupEnd (only in development)
   */
  groupEnd: () => void;
}

/**
 * Determines if console logging should be enabled
 * Checks multiple conditions for development environments
 */
const shouldLog = (): boolean => {
  // Check if we're in local development (NODE_ENV=development + localhost)
  if (isLocalDevelopment()) return true;

  // Additional check for NEXT_PUBLIC_APP_ENV=development (regardless of NODE_ENV)
  const appEnv = process.env.NEXT_PUBLIC_APP_ENV;
  if (appEnv === 'development') return true;

  // Check if debug logging is explicitly enabled
  const debugEnabled = process.env.NEXT_PUBLIC_VERBOSE_LOGGING === 'true';
  if (debugEnabled) return true;

  // Default to false for production environments
  return false;
};

/**
 * Safe console logging function that only logs in development environments
 * Includes timestamp and environment information for better debugging
 */
const createSafeLogger = (method: keyof Console) => {
  return (...args: unknown[]): void => {
    if (!shouldLog()) return;

    const timestamp = new Date().toISOString();
    const env = process.env.NEXT_PUBLIC_APP_ENV || process.env.NODE_ENV || 'unknown';
    const prefix = `[${timestamp}] [${env.toUpperCase()}]`;

    // Use the original console method
    (console[method] as (...args: unknown[]) => void)(prefix, ...args);
  };
};

/**
 * Development-only console logger
 * 
 * Features:
 * - Only logs in development/local environments
 * - Automatically includes timestamp and environment info
 * - Supports all standard console methods
 * - Custom prefix support for organized logging
 * 
 * @example
 * ```typescript
 * import { consoleLog } from '@/utils/console-log';
 * 
 * consoleLog.log('This will only appear in development');
 * consoleLog.warn('API response time slow:', responseTime);
 * consoleLog.error('Authentication failed:', error);
 * consoleLog.logWithPrefix('🔧 AUTH', 'User logged in:', user.name);
 * ```
 */
export const consoleLog: DevConsoleLog = {
  log: createSafeLogger('log'),
  warn: createSafeLogger('warn'),
  error: createSafeLogger('error'),
  debug: createSafeLogger('debug'),
  info: createSafeLogger('info'),

  logWithPrefix: (prefix: string, ...args: unknown[]): void => {
    if (!shouldLog()) return;

    const timestamp = new Date().toISOString();
    const env = process.env.NEXT_PUBLIC_APP_ENV || process.env.NODE_ENV || 'unknown';
    const fullPrefix = `[${timestamp}] [${env.toUpperCase()}] ${prefix}`;

    console.log(fullPrefix, ...args);
  },

  group: (...args: unknown[]): void => {
    if (!shouldLog()) return;
    const timestamp = new Date().toISOString();
    const env = process.env.NEXT_PUBLIC_APP_ENV || process.env.NODE_ENV || 'unknown';
    const prefix = `[${timestamp}] [${env.toUpperCase()}]`;
    console.group(prefix, ...args);
  },

  groupEnd: (...args: unknown[]): void => {
    if (!shouldLog()) return;
    console.groupEnd();
  },
};

/**
 * Utility function to check if development logging is enabled
 * Useful for conditional logic around expensive debugging operations
 * 
 * @example
 * ```typescript
 * import { isDevLoggingEnabled } from '@/utils/console-log';
 * 
 * if (isDevLoggingEnabled()) {
 *   // Only run expensive debug operations in development
 *   const debugData = expensiveDebugFunction();
 *   consoleLog.debug('Debug data:', debugData);
 * }
 * ```
 */
export const isDevLoggingEnabled = shouldLog;

/**
 * Quick development logger with emoji prefixes for visual categorization
 */
export const quickLog = {
  /** 🔧 Technical/debugging info */
  tech: (...args: unknown[]) => consoleLog.logWithPrefix('🔧', ...args),
  
  /** 🚀 Performance/optimization info */
  perf: (...args: unknown[]) => consoleLog.logWithPrefix('🚀', ...args),
  
  /** 🔐 Authentication/security info */
  auth: (...args: unknown[]) => consoleLog.logWithPrefix('🔐', ...args),
  
  /** 📡 API/network requests */
  api: (...args: unknown[]) => consoleLog.logWithPrefix('📡', ...args),
  
  /** 🎯 User interactions/events */
  user: (...args: unknown[]) => consoleLog.logWithPrefix('🎯', ...args),
  
  /** 📊 Data/state changes */
  data: (...args: unknown[]) => consoleLog.logWithPrefix('📊', ...args),
  
  /** 🧪 Testing/experimental features */
  test: (...args: unknown[]) => consoleLog.logWithPrefix('🧪', ...args),
  
  /** Quicklog grouping */
  group: (...args: unknown[]) => consoleLog.group(...args),
  groupEnd: () => consoleLog.groupEnd(),
};